import {
  ImageGeneratorCatalogColourStory,
  ImageGeneratorCatalogMacro,
  ImageGeneratorCatalogPatternTrend,
  ImageGeneratorReportPage,
} from "../../app/api/imageGenerator";

export type ImageGeneratorReportSlide = {
  id: string;
  name: string;
  url: string;
};

const pushUnique = (
  out: ImageGeneratorReportSlide[],
  seen: Set<string>,
  slide: ImageGeneratorReportSlide
) => {
  const url = slide.url.trim();
  if (!url || seen.has(url)) {
    return;
  }
  seen.add(url);
  out.push({ ...slide, url });
};

/** Macro View report — colour + pattern deck pages from catalog. */
export const reportSlidesFromPages = (
  pages: ImageGeneratorReportPage[] | null | undefined
): ImageGeneratorReportSlide[] => {
  if (!Array.isArray(pages) || !pages.length) {
    return [];
  }
  const out: ImageGeneratorReportSlide[] = [];
  const seen = new Set<string>();
  pages.forEach((page, index) => {
    const url = String(page?.url || "").trim();
    if (!url) {
      return;
    }
    pushUnique(out, seen, {
      id: String(page.id || `page-${index + 1}`),
      name: String(page.name || `Page ${index + 1}`),
      url,
    });
  });
  return out;
};

/**
 * Colour / micro View report — only the item's own `sliderImageUrl`.
 * Missing URL → empty (hide View report); never fall back to parent deck.
 */
export const reportSlidesForItem = (
  item: {
    id?: string;
    title?: string;
    name?: string;
    sliderImageUrl?: string | null;
  } | null
): ImageGeneratorReportSlide[] => {
  if (!item) {
    return [];
  }
  const url = String(item.sliderImageUrl || "").trim();
  if (!url) {
    return [];
  }
  const id = String(item.id || "").trim() || url;
  const name = String(item.title || item.name || "Report slide").trim();
  return [{ id, name: name || "Report slide", url }];
};

const reportPagesSignature = (
  pages: ImageGeneratorReportPage[] | null | undefined
): string =>
  (pages || [])
    .map(page => String(page?.url || "").trim())
    .filter(Boolean)
    .join("|");

const itemSlideIndices = (
  items:
    | Array<{ sliderImageIndex?: number | null } | null | undefined>
    | undefined
): Set<number> => {
  const out = new Set<number>();
  (items || []).forEach(item => {
    if (!item || typeof item !== "object") {
      return;
    }
    const raw = item.sliderImageIndex;
    if (raw == null || !Number.isFinite(Number(raw))) {
      return;
    }
    out.add(Number(raw));
  });
  return out;
};

const collectUniqueSlides = (
  groups: ImageGeneratorReportSlide[][]
): ImageGeneratorReportSlide[] => {
  const out: ImageGeneratorReportSlide[] = [];
  const seen = new Set<string>();
  groups.forEach(group => {
    group.forEach(slide => pushUnique(out, seen, slide));
  });
  return out;
};

const slidesFromItemOwnPages = (
  items:
    | Array<{ pages?: ImageGeneratorReportPage[] | null } | null | undefined>
    | undefined,
  themePages: ImageGeneratorReportPage[] | null | undefined
): ImageGeneratorReportSlide[] => {
  const parentSig = reportPagesSignature(themePages);
  const groups: ImageGeneratorReportSlide[][] = [];
  (items || []).forEach(item => {
    if (!item || typeof item !== "object") {
      return;
    }
    const ownPages = item.pages;
    const ownSig = reportPagesSignature(ownPages);
    if (ownSig && ownSig !== parentSig) {
      groups.push(reportSlidesFromPages(ownPages));
    }
  });
  return collectUniqueSlides(groups);
};

const slidesFromItemUrls = (
  items:
    | Array<{
        id?: string;
        title?: string;
        name?: string;
        sliderImageUrl?: string | null;
      } | null>
    | undefined
): ImageGeneratorReportSlide[] =>
  collectUniqueSlides((items || []).map(item => reportSlidesForItem(item)));

const slidesFromThemePagesByIndex = (
  pages: ImageGeneratorReportPage[] | null | undefined,
  keep: Set<number> | null,
  exclude: Set<number>
): ImageGeneratorReportSlide[] => {
  const list = Array.isArray(pages) ? pages : [];
  const filtered = list.filter((page, index) => {
    if (!page) {
      return false;
    }
    const raw = page.sliderImageIndex;
    const n =
      raw != null && Number.isFinite(Number(raw)) ? Number(raw) : index + 1;
    if (keep && keep.size) {
      return keep.has(n);
    }
    return !exclude.has(n);
  });
  return reportSlidesFromPages(filtered);
};

/**
 * Macro Theme View report — colour stories only (no pattern-trend slides).
 * Matches Collection Build’s theme-level colour deck.
 */
export const reportSlidesForMacroThemeColour = (
  theme:
    | (Pick<ImageGeneratorCatalogMacro, "pages" | "colourStories"> & {
        patternTrends?: ImageGeneratorCatalogPatternTrend[];
        colourStories?: ImageGeneratorCatalogColourStory[];
      })
    | null
    | undefined
): ImageGeneratorReportSlide[] => {
  if (!theme || typeof theme !== "object") {
    return [];
  }
  const colourStories = theme.colourStories || [];
  const fromOwnPages = slidesFromItemOwnPages(colourStories, theme.pages);
  if (fromOwnPages.length) {
    return fromOwnPages;
  }
  const colourIdx = itemSlideIndices(colourStories);
  const patternIdx = itemSlideIndices(theme.patternTrends);
  const fromParent = colourIdx.size
    ? slidesFromThemePagesByIndex(theme.pages, colourIdx, new Set())
    : patternIdx.size
      ? slidesFromThemePagesByIndex(theme.pages, null, patternIdx)
      : [];
  if (fromParent.length) {
    return fromParent;
  }
  return slidesFromItemUrls(colourStories);
};
